import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { getFakePermitAction } from '../flavours/permit/logic/getFakePermitAction'
import { useCreatePermitHandler } from '../flavours/permit/logic/useCreatePermitHandler'
import { Action, ActionContext, ActionHandler } from './types'
import { ACTION_QUERY_PREFIX, useContractAction } from './useContractAction'

export interface UseActionHandlersParams {
  actions: Action[]
  onFinish: () => void
  enabled: boolean
  context: ActionContext
  actionsInvocationId: string
}

export interface UseActionHandlersResult {
  actionHandlers: ActionHandler[]
  actionsInProgress: boolean // @note: after first interaction, we don't enable for settings to change
  reset: () => void
}

export function useActionHandlers({
  actions,
  onFinish,
  enabled,
  context,
  actionsInvocationId,
}: UseActionHandlersParams): UseActionHandlersResult {
  const queryClient = useQueryClient()
  const [currentActionIndex, setCurrentActionIndex] = useState(0)
  const hasActions = actions.length > 0

  // If we have no actions, we pretend there is a single permit actions so we don't break types.
  // Afterwards we replace action hadnlers list with an empty list.
  // permit is a good candidate because it doesn't send anything onchain in case of something goes
  // wrong.
  const currentAction = hasActions ? actions[currentActionIndex]! : getFakePermitAction()
  const currentActionIsPermit = currentAction?.type === 'permit'

  const actionHandlers: ActionHandler[] = actions.map((action, index) => ({
    action,
    onAction: () => {},
    state: { status: index === currentActionIndex ? 'ready' : index < currentActionIndex ? 'success' : 'disabled' },
  }))

  const handler = useContractAction({
    action: currentAction,
    actionId: `${actionsInvocationId}-${currentActionIndex}`,
    context,
    enabled: hasActions && !currentActionIsPermit && enabled,
  })

  const permitHandler = useCreatePermitHandler(currentActionIsPermit ? currentAction : getFakePermitAction(), {
    enabled: hasActions && enabled && currentActionIsPermit,
    permitStore: context.permitStore,
    mutationKeyPrefix: ['permit', `${actionsInvocationId}-${currentActionIndex}`] as const,
  })

  const currentActionHandler = currentActionIsPermit ? permitHandler : handler

  useEffect(() => {
    if (!hasActions) {
      return
    }

    if (currentActionHandler?.state.status !== 'success') {
      return
    }

    if (currentActionIndex === actions.length - 1) {
      onFinish?.()
    } else {
      setCurrentActionIndex(currentActionIndex + 1)
    }
  }, [hasActions, currentActionIndex, actions.length, onFinish, currentActionHandler?.state.status])

  if (currentActionHandler) {
    actionHandlers[currentActionIndex] = currentActionHandler
  }

  const actionsInProgress = hasActions && currentActionIndex > 0

  const reset = useCallback(() => {
    setCurrentActionIndex(0)
    void queryClient.resetQueries({ queryKey: [ACTION_QUERY_PREFIX] })
  }, [queryClient.resetQueries])

  if (!hasActions) {
    return {
      actionHandlers: [],
      actionsInProgress: false,
      reset,
    }
  }

  return {
    actionHandlers,
    actionsInProgress,
    reset,
  }
}
